import { memo, useEffect, useState } from "react";
import { Button, Form, Modal, Spinner } from "react-bootstrap";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useGetAttendeeNoteForAttendee } from "../../api/queries/useGetAttendeeNoteForAttendee";
import { useSaveAttendeeNote } from "../../api/mutations/useSaveAttendeeNote";
import { useDeleteAttendeeNote } from "../../api/mutations/useDeleteAttendeeNote";
import { User } from "../../types/Types";
import { resolvedObjectId } from "../../utils/connectionUserUtils";

const BTN_HEIGHT = 38;

const ACTION_BTN_BASE: React.CSSProperties = {
  height: BTN_HEIGHT,
  minHeight: BTN_HEIGHT,
  padding: "0 16px",
  fontSize: 12.5,
  lineHeight: 1.2,
  fontWeight: 500,
  boxShadow: "0 0.125rem 0.25rem rgba(0, 0, 0, 0.075)",
};

const PRIMARY_BTN_STYLE: React.CSSProperties = {
  ...ACTION_BTN_BASE,
  background: "linear-gradient(90deg, #2366CD 0%, #3A97A4 100%)",
  border: "none",
  color: "#fff",
};

const SECONDARY_BTN_STYLE: React.CSSProperties = {
  ...ACTION_BTN_BASE,
  background: "#f8f9fa",
  border: "none",
  color: "#212529",
};

const DANGER_BTN_STYLE: React.CSSProperties = {
  ...ACTION_BTN_BASE,
  background: "#fff",
  border: "1px solid #dc3545",
  color: "#dc3545",
};

export interface SaveAttendeeNoteModalProps {
  show: boolean;
  onHide: () => void;
  eventId: string | number;
  eventName?: string;
  attendee: User | null;
  /** When true, always create a new note (skip load/update of an existing one). */
  alwaysCreate?: boolean;
}

function SaveAttendeeNoteModal({
  show,
  onHide,
  eventId,
  eventName,
  attendee,
  alwaysCreate = false,
}: SaveAttendeeNoteModalProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const attendeeObjectId = attendee ? resolvedObjectId(attendee) : "";
  const attendeeName = attendee
    ? `${attendee.firstName ?? ""} ${attendee.lastName ?? ""}`.trim()
    : "";
  const isPersonalCreate = alwaysCreate || Number(eventId) === 0;

  const [noteText, setNoteText] = useState("");
  const [existingId, setExistingId] = useState<number | null>(null);

  const { data: existingNote, isLoading: loadingNote } =
    useGetAttendeeNoteForAttendee({
      eventId,
      attendeeObjectId,
      enabled: show && !!attendeeObjectId && !isPersonalCreate,
    });

  useEffect(() => {
    if (!show) {
      setNoteText("");
      setExistingId(null);
      return;
    }
    if (isPersonalCreate) {
      setNoteText("");
      setExistingId(null);
      return;
    }
    if (existingNote) {
      setNoteText(existingNote.noteText ?? "");
      setExistingId(existingNote.id);
    } else if (!loadingNote) {
      setNoteText("");
      setExistingId(null);
    }
  }, [show, existingNote, loadingNote, isPersonalCreate]);

  const invalidateNotes = () => {
    queryClient.invalidateQueries({ queryKey: ["attendeeNotes"] });
  };

  const { mutate: saveNote, isPending: saving } = useSaveAttendeeNote({
    onSuccess: () => {
      toast.success(
        existingId
          ? t("attendeeNotes.updatedSuccess")
          : t("attendeeNotes.savedSuccess")
      );
      invalidateNotes();
      onHide();
    },
    onError: () => toast.error(t("attendeeNotes.saveError")),
  });

  const { mutate: deleteNote, isPending: deleting } = useDeleteAttendeeNote({
    onSuccess: () => {
      toast.success(t("attendeeNotes.deletedSuccess"));
      invalidateNotes();
      onHide();
    },
    onError: () => toast.error(t("attendeeNotes.deleteError")),
  });

  const handleSave = () => {
    const text = noteText.trim();
    if (!text) {
      toast.error(t("attendeeNotes.noteRequired"));
      return;
    }
    if (!attendeeObjectId) {
      toast.error(t("attendeeNotes.attendeeMissing"));
      return;
    }

    if (existingId && !isPersonalCreate) {
      saveNote({ id: existingId, noteText: text });
    } else {
      saveNote({
        eventId: Number(eventId) || 0,
        attendeeObjectId,
        noteText: text,
      });
    }
  };

  const handleDelete = () => {
    if (!existingId) return;
    deleteNote(existingId);
  };

  const busy = saving || deleting;

  return (
    <Modal show={show} onHide={busy ? undefined : onHide} centered>
      <Modal.Header closeButton={!busy}>
        <Modal.Title>
          {existingId
            ? t("attendeeNotes.editTitle")
            : t("attendeeNotes.addTitle")}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {attendeeName ? (
          <p className="mb-1">
            <span className="text-muted small">{t("attendeeNotes.about")}: </span>
            <strong>
              {Number(eventId) === 0
                ? t("attendeeNotes.you")
                : attendeeName}
            </strong>
          </p>
        ) : null}
        {Number(eventId) === 0 ? (
          <p className="mb-3 text-muted small">{t("attendeeNotes.addOwnHint")}</p>
        ) : eventName ? (
          <p className="mb-3 text-muted small">
            {t("attendeeNotes.event")}: {eventName}
          </p>
        ) : (
          <div className="mb-3" />
        )}
        {Number(eventId) !== 0 ? (
          <p className="text-muted small mb-2">{t("attendeeNotes.hint")}</p>
        ) : null}
        {loadingNote && !isPersonalCreate ? (
          <div className="text-center py-4">
            <Spinner animation="border" size="sm" />
          </div>
        ) : (
          <Form.Control
            as="textarea"
            rows={5}
            maxLength={4000}
            value={noteText}
            disabled={busy}
            placeholder={
              isPersonalCreate
                ? t("attendeeNotes.personalPlaceholder")
                : t("attendeeNotes.placeholder")
            }
            onChange={(e) => setNoteText(e.target.value)}
          />
        )}
        <div className="text-end text-muted small mt-1">
          {noteText.length}/4000
        </div>
      </Modal.Body>
      <Modal.Footer className="d-flex justify-content-between">
        <div>
          {existingId && !isPersonalCreate ? (
            <Button
              className="rounded-2 shadow-sm fw-medium"
              style={DANGER_BTN_STYLE}
              disabled={busy || loadingNote}
              onClick={handleDelete}
            >
              {deleting ? t("attendeeNotes.deleting") : t("attendeeNotes.delete")}
            </Button>
          ) : null}
        </div>
        <div className="d-flex gap-2">
          <Button
            className="rounded-2 border-0 shadow-sm fw-medium text-dark"
            style={SECONDARY_BTN_STYLE}
            disabled={busy}
            onClick={onHide}
          >
            {t("attendeeNotes.cancel")}
          </Button>
          <Button
            className="rounded-2 border-0 shadow-sm fw-medium text-white"
            style={PRIMARY_BTN_STYLE}
            disabled={busy || (loadingNote && !isPersonalCreate) || !noteText.trim()}
            onClick={handleSave}
          >
            {saving ? t("attendeeNotes.saving") : t("attendeeNotes.save")}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
}

export default memo(SaveAttendeeNoteModal);
